import React, { useMemo, useState } from "react";
import { css } from "@emotion/css";
import { MathMarkdown, MathText } from "../../components/MathMarkdown";
import { PendingIndicator } from "../../components/PendingIndicator";
import type { ArcCapabilitySetSpec } from "../../types/arcGenerationSession";

// ── Styles ──────────────────────────────────────────────────────────

const readbackStyle = css`
  background: var(--color-coach-bubble-bg);
  color: var(--color-coach-bubble-text);
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 0.97em;
  line-height: 1.55;
  margin-bottom: 14px;
  p { margin: 0 0 10px 0; }
  p:last-child { margin-bottom: 0; }
  strong, b { color: var(--color-text); }
`;

const atlasWrapStyle = css`
  border: 1px solid var(--color-border-subtle);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 18px;
  background: var(--color-bg);
`;

const atlasTierLabelStyle = css`
  font-size: 0.74em;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin: 10px 0 6px;
  &:first-of-type { margin-top: 0; }
`;

const chipRowStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const chipStyle = css`
  font-size: 0.8em;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--color-bg-raised);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-subtle);
`;

const chipPrimaryStyle = css`
  background: var(--color-primary-muted);
  color: var(--color-text);
  border-color: var(--color-primary);
`;

const viewFullBtnStyle = css`
  margin-top: 12px;
  background: none;
  border: none;
  color: var(--color-primary);
  font-size: 0.84em;
  cursor: pointer;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const promptStyle = css`
  font-size: 0.86em;
  color: var(--color-text-secondary);
  margin-bottom: 10px;
  line-height: 1.45;
`;

const capListStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const capCardStyle = css`
  text-align: left;
  border: 1px solid var(--color-border);
  border-radius: 14px;
  background: var(--color-bg-elevated);
  padding: 16px 18px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  &:hover:not([aria-disabled="true"]) { border-color: var(--color-primary); }
`;

const capCardSelectedStyle = css`
  border-color: var(--color-primary) !important;
  background: var(--color-primary-muted) !important;
  box-shadow: 0 0 0 1px var(--color-primary) inset;
`;

const capTitleStyle = css`
  font-size: 1.05em;
  font-weight: 700;
  line-height: 1.3;
  color: var(--color-text);
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const capPromiseStyle = css`
  font-size: 0.9em;
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin-top: 6px;
`;

const capMetaLabelStyle = css`
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin-top: 12px;
`;

const capMetaStyle = css`
  font-size: 0.85em;
  color: var(--color-text);
  line-height: 1.45;
  margin-top: 3px;
`;

const capRegionsStyle = css`
  font-size: 0.78em;
  color: var(--color-text-muted);
  margin-top: 10px;
`;

const checkStyle = css`
  color: var(--color-primary);
  width: 14px;
  flex-shrink: 0;
`;

const startBtnStyle = css`
  width: 100%;
  margin-top: 16px;
  padding: 13px;
  border: none;
  border-radius: 10px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 1em;
  font-weight: 650;
  cursor: pointer;
  &:hover:not(:disabled) { background: var(--color-primary-hover); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const TIER_LABELS: Record<string, string> = {
  primary: "Primary territory",
  adjacent: "Adjacent support",
  later: "Later / less central for now",
};

// ── Component ───────────────────────────────────────────────────────

export interface CapabilitySetProps {
  spec: ArcCapabilitySetSpec;
  isStreaming: boolean;
  disabled?: boolean;
  onChoose: (capabilityId: string, capabilityTitle: string) => void;
}

/**
 * Coach readback + atlas slice + capability candidates on one screen
 * (onboarding phases 3 & 4 combined).  The learner picks one capability
 * to start; the atlas slice grounds the path without overwhelming.
 */
export const CapabilitySet: React.FC<CapabilitySetProps> = ({
  spec, isStreaming, disabled, onChoose,
}) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [showFullAtlas, setShowFullAtlas] = useState(false);

  const regions = spec.regions ?? [];
  const capabilities = spec.capabilities ?? [];

  const tiers = useMemo(() => {
    const byTier: Record<string, string[]> = {};
    for (const r of regions) {
      const t = (r.tier || "later").toLowerCase();
      (byTier[t] ??= []).push(r.name);
    }
    return byTier;
  }, [regions]);

  const visibleTiers = showFullAtlas ? ["primary", "adjacent", "later"] : ["primary"];
  const hasMoreTiers = (tiers.adjacent?.length ?? 0) + (tiers.later?.length ?? 0) > 0;

  const selectedTitle =
    capabilities.find((c) => c.capabilityId === selected)?.title ?? "";

  if (isStreaming && capabilities.length === 0) {
    return (
      <PendingIndicator
        variant="block"
        label="Synthesizing what I'm hearing…"
        data-testid="capability-set-loading"
      />
    );
  }

  return (
    <div data-testid="capability-set">
      {/* Coach readback */}
      {spec.readback ? (
        <div className={readbackStyle} data-testid="capability-readback">
          <MathMarkdown>{spec.readback}</MathMarkdown>
        </div>
      ) : null}

      {/* Atlas slice (focused; "View full atlas" expands) */}
      {regions.length > 0 && (
        <div className={atlasWrapStyle} data-testid="capability-atlas-slice">
          {visibleTiers.map((tier) =>
            (tiers[tier]?.length ?? 0) > 0 ? (
              <div key={tier}>
                <div className={atlasTierLabelStyle}>{TIER_LABELS[tier] ?? tier}</div>
                <div className={chipRowStyle}>
                  {tiers[tier].map((name) => (
                    <span
                      key={name}
                      className={tier === "primary" ? `${chipStyle} ${chipPrimaryStyle}` : chipStyle}
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            ) : null
          )}
          {hasMoreTiers && (
            <button
              type="button"
              className={viewFullBtnStyle}
              onClick={() => setShowFullAtlas((v) => !v)}
              data-testid="capability-view-atlas"
            >
              {showFullAtlas ? "Hide full atlas" : "View full atlas →"}
            </button>
          )}
        </div>
      )}

      {/* Capability candidates */}
      <div className={promptStyle}>
        Here are the strongest paths I can build for you. Pick the one that feels
        most worth starting — you can branch later.
      </div>
      <div className={capListStyle} data-testid="capability-cards">
        {capabilities.map((c) => {
          const sel = selected === c.capabilityId;
          return (
            <div
              key={c.capabilityId}
              role="button"
              tabIndex={0}
              aria-pressed={sel}
              aria-disabled={disabled ? "true" : "false"}
              className={sel ? `${capCardStyle} ${capCardSelectedStyle}` : capCardStyle}
              onClick={() => !disabled && setSelected(c.capabilityId)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !disabled) {
                  e.preventDefault();
                  setSelected(c.capabilityId);
                }
              }}
              data-testid={`capability-card-${c.capabilityId}`}
              data-selected={sel ? "true" : "false"}
            >
              <div className={capTitleStyle}>
                <span className={checkStyle}>{sel ? "✓" : ""}</span>
                <span><MathText text={c.title} /></span>
              </div>
              {c.promise ? <div className={capPromiseStyle}>{c.promise}</div> : null}
              {c.whyItFits ? (
                <>
                  <div className={capMetaLabelStyle}>Why it fits</div>
                  <div className={capMetaStyle}>{c.whyItFits}</div>
                </>
              ) : null}
              {c.proofTarget ? (
                <>
                  <div className={capMetaLabelStyle}>You'll be able to prove</div>
                  <div className={capMetaStyle}>{c.proofTarget}</div>
                </>
              ) : null}
              {(c.primaryRegions || c.supportRegions) ? (
                <div className={capRegionsStyle}>
                  Atlas: {[c.primaryRegions, c.supportRegions].filter(Boolean).join(" · ")}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className={startBtnStyle}
        disabled={disabled || !selected}
        data-testid="capability-start"
        onClick={() => selected && onChoose(selected, selectedTitle)}
      >
        {selected ? "Calibrate this path →" : "Pick a path to start"}
      </button>
    </div>
  );
};
